/**
 * dongniai 前台 PV/UV 埋点 SDK
 *
 * 特点：
 * - L1 防御：异步 fetch，不阻塞渲染
 * - L2 防御：所有异常 try/catch 吞掉，埋点失败不影响主流程
 * - L3 防御：重试 1 次，避免网络抖动漏数据
 * - L4 防御：本地存储 session_id，30 分钟无活动算新会话
 * - L5 防御：< 3KB gzip，bundle 友好
 *
 * 用法：
 *   import { initTracking, trackPageView, trackEvent } from '@/lib/tracking';
 *   initTracking();  // 在 layout 调一次
 *   trackPageView(); // 路由切换时调
 *
 * @date 2026-07-15
 */

'use client';

const STORAGE_KEY = 'dongniai_session';
const LAST_ACTIVE_KEY = 'dongniai_last_active';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 分钟
const PING_INTERVAL_MS = 30 * 1000; // 30 秒心跳（标记活动）
const TRACK_INTERVAL_MS = 5 * 1000; // 节流 PV 上报（避免重复）

let isInitialized = false;
let lastTrackUrl: string | null = null;
let lastTrackTime = 0;

/**
 * 生成 UUID v4（用 crypto.randomUUID）
 */
function generateUuid(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // 降级方案
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * 获取或创建 session_id
 * - 新会话：超过 30 分钟无活动 → 新 session
 * - 新会话：首次访问 → 新 session
 */
function getOrCreateSession(): string {
  if (typeof window === 'undefined') return '';

  try {
    const now = Date.now();
    const existing = localStorage.getItem(STORAGE_KEY);
    const lastActive = parseInt(localStorage.getItem(LAST_ACTIVE_KEY) || '0', 10);

    if (existing && (now - lastActive) < SESSION_TIMEOUT_MS) {
      // 复用
      localStorage.setItem(LAST_ACTIVE_KEY, String(now));
      return existing;
    }

    // 新会话
    const newSession = generateUuid();
    localStorage.setItem(STORAGE_KEY, newSession);
    localStorage.setItem(LAST_ACTIVE_KEY, String(now));
    return newSession;
  } catch {
    // localStorage 不可用（隐身模式 / Safari ITP）→ 降级为内存
    return generateUuid();
  }
}

/**
 * 获取 UTM 参数
 */
function getUtmParams(): { source: string; medium: string; campaign: string } {
  if (typeof window === 'undefined') return { source: '', medium: '', campaign: '' };
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      source: params.get('utm_source') || '',
      medium: params.get('utm_medium') || '',
      campaign: params.get('utm_campaign') || '',
    };
  } catch {
    return { source: '', medium: '', campaign: '' };
  }
}

/**
 * 解析 Referrer 域名
 */
function getReferrerDomain(referrer: string): string {
  if (!referrer) return '';
  try {
    const url = new URL(referrer);
    return url.hostname;
  } catch {
    return '';
  }
}

/**
 * 简单 UA 解析（不依赖外部库）
 */
function parseUserAgent(ua: string): { browser: string; os: string; deviceType: string } {
  let browser = 'Unknown';
  let os = 'Unknown';
  let deviceType = 'pc';

  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua)) browser = 'Safari';
  else if (/MSIE|Trident\//.test(ua)) browser = 'IE';

  if (/Windows/.test(ua)) os = 'Windows';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua)) os = 'Linux';

  if (/Mobile|Tablet|Android|iPhone|iPad/.test(ua)) {
    deviceType = /Tablet|iPad/.test(ua) ? 'tablet' : 'mobile';
  }

  return { browser, os, deviceType };
}

/**
 * 获取当前用户 ID（如果有登录态）
 * 通过查找 cookie 或 localStorage 中的 user_id
 */
function getCurrentUserId(): number {
  if (typeof window === 'undefined') return 0;
  try {
    // FastAdmin / dongniai 的登录 cookie 名
    const match = document.cookie.match(/think_[\w]+_user_id=(\d+)/) ||
                  document.cookie.match(/uid=(\d+)/);
    if (match) return parseInt(match[1], 10);
    return 0;
  } catch {
    return 0;
  }
}

/**
 * 构建埋点 payload
 */
function buildPayload(extra: Record<string, any> = {}): Record<string, any> {
  const sessionId = getOrCreateSession();
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const { browser, os, deviceType } = parseUserAgent(ua);
  const utm = getUtmParams();

  let screenW = 0, screenH = 0, language = '';
  if (typeof window !== 'undefined') {
    screenW = window.screen?.width || 0;
    screenH = window.screen?.height || 0;
    language = navigator.language || '';
  }

  let referrer = '', currentUrl = '', urlPath = '', entryUrl = sessionStorage.getItem('dongniai_entry_url') || '';
  if (typeof document !== 'undefined') {
    referrer = document.referrer || '';
    currentUrl = window.location.href;
    urlPath = window.location.pathname;
    if (!entryUrl) {
      entryUrl = currentUrl;
      try { sessionStorage.setItem('dongniai_entry_url', entryUrl); } catch {}
    }
  }

  return {
    session_id: sessionId,
    user_id: getCurrentUserId(),
    url: currentUrl,
    url_path: urlPath,
    entry_url: entryUrl,
    referrer,
    referrer_domain: getReferrerDomain(referrer),
    utm_source: utm.source,
    utm_medium: utm.medium,
    utm_campaign: utm.campaign,
    device_type: deviceType,
    browser,
    os,
    screen_w: screenW,
    screen_h: screenH,
    language,
    created_at: Date.now(),
    duration_ms: 0,
    is_bounce: 0,
    ...extra,
  };
}

/**
 * 异步上报埋点（不 await，不阻塞）
 */
function sendTracking(payload: Record<string, any>, retries = 1): void {
  if (typeof window === 'undefined') return;
  if (typeof fetch === 'undefined') return;

  try {
    // 异步 fetch，忽略响应
    // 同源 POST 到 PHP 公开端点 (traffic_public.php)，无需 Next.js 中转
    fetch('/traffic_public.php?method=track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
      mode: 'same-origin',
      credentials: 'same-origin',
    }).catch(() => {
      // 失败时重试 1 次
      if (retries > 0) {
        setTimeout(() => sendTracking(payload, retries - 1), 2000);
      }
    });
  } catch {
    // 完全失败也不抛错
  }
}

/**
 * 上报一次 PV（节流：5 秒内同 URL 不重复）
 */
export function trackPageView(extra: Record<string, any> = {}): void {
  if (typeof window === 'undefined') return;

  const now = Date.now();
  const currentUrl = window.location.pathname + window.location.search;

  // 节流：同 URL 5 秒内不重复
  if (lastTrackUrl === currentUrl && (now - lastTrackTime) < TRACK_INTERVAL_MS) {
    return;
  }

  lastTrackUrl = currentUrl;
  lastTrackTime = now;

  const payload = buildPayload(extra);
  sendTracking(payload);
}

/**
 * 上报自定义事件（保留接口，可扩展）
 */
export function trackEvent(eventName: string, data: Record<string, any> = {}): void {
  if (typeof window === 'undefined') return;
  sendTracking({
    ...buildPayload(),
    event_name: eventName,
    event_data: data,
  });
}

/**
 * 初始化埋点（在 layout 调一次即可）
 */
export function initTracking(): void {
  if (typeof window === 'undefined') return;
  if (isInitialized) return;
  isInitialized = true;

  // 首次 PV
  trackPageView();

  // 心跳：每 30 秒标记活动 + 上报持续时间
  setInterval(() => {
    try {
      localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
      // 上报当前页面停留时长
      const payload = buildPayload({
        duration_ms: TRACK_INTERVAL_MS, // 30 秒一次心跳
      });
      sendTracking(payload);
    } catch {
      // 静默失败
    }
  }, PING_INTERVAL_MS);

  // 路由变化（History API）
  if (typeof window !== 'undefined') {
    const origPushState = window.history.pushState;
    window.history.pushState = function(...args) {
      const r = origPushState.apply(this, args as any);
      // 异步避免阻塞
      setTimeout(() => trackPageView(), 0);
      return r;
    };
    window.addEventListener('popstate', () => {
      setTimeout(() => trackPageView(), 0);
    });
  }

  // 离开页面前上报
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      const payload = buildPayload();
      payload.is_bounce = 1; // 未触发其他事件 ≈ 跳出
      // 用 sendBeacon（如果支持）
      if (typeof navigator.sendBeacon === 'function') {
        try {
          navigator.sendBeacon(
            '/traffic_public.php?method=track',
            new Blob([JSON.stringify(payload)], { type: 'application/json' })
          );
        } catch {}
      } else {
        sendTracking(payload);
      }
    });
  }
}
